import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {DatasetColumnDataType, DatasetColumnRole} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {Dataset} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {OrganizationalUnitStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {QueryClient} from "@tanstack/react-query";
import {AnalyticsQueryVariant, type DataResolverProps, type RawData} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import {fetchEratoDatasetData, getEratoDatasetDetails} from "~/workspaceConfig/hostedPages/dashboard/-api";
import type {AnalyticalEngineDataTransformationContext} from "~/queries/analyticalEngine/analyticalEngineQueries";
import {DashboardRowType, type DashboardConfig} from "~/workspaceConfig/hostedPages/dashboard/-types";
import {WidgetType, type Widget} from "~/components/analyticsWidgets/widgetTypes/widget";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {DuluxRoles} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Users";
import {DuluxOus} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/OrganizationalUnits";

/** Erato dataset used by the Dulux dashboard. */
export const DULUX_DASHBOARD_DATASET_ID = "17ab6e4b-daac-4eb4-820f-9b77998201f6" as Uuid;
/** AID dataset used by the second KPI row and its supporting trend charts. */
export const DULUX_AID_DASHBOARD_DATASET_ID = "5d1ab32f-11f1-40eb-94af-95f08bff127e" as Uuid;

const DULUX_COLUMN_IDS = {
    walkins: "084f687d-e48d-4f9e-b834-4a290fddb211" as Uuid,
    leads: "153e95e2-4fb2-4851-9c8e-6ac131a40dbd" as Uuid,
    calls: "45b52e34-e187-4272-aef6-c25b0f2439ad" as Uuid,
    quotations: "d97b17cf-a139-48eb-8077-e59e48e1231b" as Uuid,
    leadToWalkinRatio: "146e4c39-832d-4c6e-bda0-68a89533d6ee" as Uuid,
    leadToCallRatio: "2d695e5d-2ba7-4314-b96f-eb6bf6c99b2f" as Uuid,
    date: "0fdde20c-9a16-4073-a46f-90adc758727a" as Uuid,
    source: "218d13c6-28b5-4d4f-961b-6205325b9740" as Uuid,
} as const;

/** Column ids from the AID dataset (date, source, and funnel counts). */
const DULUX_AID_COLUMN_IDS = {
    // Metric ids are mapped to their rendered AID metadata labels.
    leads: "8fcde9a6-86f0-4655-9a41-7b61e622a229" as Uuid,
    totalEnquiries: "4aa8caa0-c84b-40f7-9de7-0b4d8d610242" as Uuid,
    onboardings: "9a333472-851a-416e-b520-a8f8cc4c128d" as Uuid,
    appointments: "0acde707-e06d-44aa-9035-5984144f35f9" as Uuid,
    calls: "e967291a-ed11-487f-93fe-e7225431938a" as Uuid,
    // The AID dataset resolves this column as the source dimension in the dashboard response.
    source: "10afe2ad-a6ec-4d16-bbab-5e127fd38a0c" as Uuid,
    pincode: "320670b3-00f1-4701-b89d-1aa13625ddf4" as Uuid,
    city: "68ed1bf6-d42f-44b6-8943-69dcecb302b2" as Uuid,
    state: "8ea629df-a0d3-466f-8adc-c178796865f5" as Uuid,
    // The AID dataset resolves this column as the date dimension in the dashboard response.
    date: "92e12bb4-d696-416b-ac97-d502c594f3ee" as Uuid,
    platform: "e4bc5302-760e-4626-b2c0-eb5bb217e07e" as Uuid,
} as const;

export type DuluxDashboardVisibility = "both" | "aidOnly" | "bluestoreOnly";

const DULUX_AID_ONLY_USER_TEMPLATE_IDS: ReadonlySet<string> = new Set([DuluxRoles.RMM, DuluxRoles.MDO, DuluxRoles.HOAID]);
const DULUX_BOTH_USER_TEMPLATE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.SystemAdministrator,
    DuluxRoles.HOCentral,
]);
const DULUX_OPERATIONAL_PERFORMANCE_USER_TEMPLATE_IDS: ReadonlySet<string> = new Set([
    DuluxRoles.SystemAdministrator,
    DuluxRoles.HOBlueStore,
]);

export function getDuluxDashboardVisibility(userTemplateId: Uuid): DuluxDashboardVisibility {
    const userTemplateIdString = String(userTemplateId);
    if (DULUX_AID_ONLY_USER_TEMPLATE_IDS.has(userTemplateIdString)) {
        return "aidOnly";
    }
    if (DULUX_BOTH_USER_TEMPLATE_IDS.has(userTemplateIdString)) {
        return "both";
    }
    return "bluestoreOnly";
}

export function filterDuluxDashboardConfigForUserTemplate(
    config: DashboardConfig,
    userTemplateId: Uuid,
): DashboardConfig {
    const visibility = getDuluxDashboardVisibility(userTemplateId);
    return {
        ...config,
        sections: config.sections.flatMap((section) => {
            if (section.title === "Bluestore Performance Trends" && visibility === "aidOnly") {
                return [];
            }
            if (section.title === "AID Performance Trends" && visibility === "bluestoreOnly") {
                return [];
            }
            if (section.title !== "Key Metrics") {
                return [section];
            }

            const rows = section.rows.filter((row) => {
                if (row.title === "Bluestore KPI cards") {
                    return visibility !== "aidOnly";
                }
                if (row.title === "Operational performance") {
                    return DULUX_OPERATIONAL_PERFORMANCE_USER_TEMPLATE_IDS.has(String(userTemplateId));
                }
                if (row.title === "AID KPI cards") {
                    return visibility !== "bluestoreOnly";
                }
                return true;
            });
            return rows.length > 0 ? [{...section, rows}] : [];
        }),
    };
}

export function getDuluxDashboardDatasets(
    dataset: Dataset,
    aidDataset: Dataset,
    userTemplateId: Uuid,
): Array<Dataset> {
    const visibility = getDuluxDashboardVisibility(userTemplateId);
    if (visibility === "aidOnly") {
        return [aidDataset];
    }
    if (visibility === "bluestoreOnly") {
        return [dataset];
    }
    return [dataset, aidDataset];
}

export function getDuluxDashboardConfig(getAdditionalColumns: CapabilityUdoColumnResolver, {
    dataset,
    aidDataset,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    queryClient,
    transformationContext,
    timeZone,
    startTime,
    endTime,
}: {
    dataset: Dataset;
    aidDataset: Dataset;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    queryClient: QueryClient;
    transformationContext: AnalyticalEngineDataTransformationContext;
    timeZone: Uuid;
    startTime: Iso8601DateTime;
    endTime: Iso8601DateTime;
}): DashboardConfig {
    if (dataset.id !== DULUX_DASHBOARD_DATASET_ID) {
        throw new Error(`Unexpected Dulux dashboard dataset: ${dataset.id}`);
    }
    const missingColumnIds = Object.values(DULUX_COLUMN_IDS).filter((columnId) => dataset.columns.some((column) => column.id === columnId) !== true);
    if (missingColumnIds.length > 0) {
        throw new Error(`Dulux dashboard dataset is missing columns: ${missingColumnIds.join(", ")}`);
    }
    if (aidDataset.id !== DULUX_AID_DASHBOARD_DATASET_ID) {
        throw new Error(`Unexpected Dulux AID dashboard dataset: ${aidDataset.id}`);
    }
    const missingAidColumnIds = [
        DULUX_AID_COLUMN_IDS.leads,
        DULUX_AID_COLUMN_IDS.totalEnquiries,
        DULUX_AID_COLUMN_IDS.onboardings,
        DULUX_AID_COLUMN_IDS.appointments,
        DULUX_AID_COLUMN_IDS.date,
        DULUX_AID_COLUMN_IDS.source,
    ].filter((columnId) => aidDataset.columns.some((column) => column.id === columnId) !== true);
    if (missingAidColumnIds.length > 0) {
        throw new Error(`Dulux AID dashboard dataset is missing columns: ${missingAidColumnIds.join(", ")}`);
    }
    const DULUX = DULUX_COLUMN_IDS;
    const AID = DULUX_AID_COLUMN_IDS;

    const partialGetDataset = (datasetId: Uuid) => getEratoDatasetDetails(datasetId, intellsysBaseUrl, accessToken, workspaceId, queryClient);
    const partialFetchData = (dataset: Dataset, props: DataResolverProps) =>
        fetchEratoDatasetData(getAdditionalColumns, intellsysBaseUrl, accessToken, workspaceId, queryClient, transformationContext, dataset, props);
    const totalBluestores = transformationContext.organizationalUnits.filter(
        (organizationalUnit) =>
            organizationalUnit.templateId === (DuluxOus.BlueStore as Uuid) && organizationalUnit.status === OrganizationalUnitStatus.active,
    ).length;
    const countActiveUsersForRole = (roleId: Uuid): number => transformationContext.users.filter(
        (user) => user.templateId === roleId && user.status === UserStatus.active,
    ).length;
    const blueStoreHoKpiCounts = {
        isd: countActiveUsersForRole(DuluxRoles.ISDBS),
        mdo: countActiveUsersForRole(DuluxRoles.BlueStoreMDO),
        rsm: countActiveUsersForRole(DuluxRoles.RSM),
    };

    const makeNumberTileWidget = (id: Uuid, title: string | null, metrics: Uuid[], valueSuffix: string = "", decimalPlaces: number = 0): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: dataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix,
    });

    const makeStaticNumberTileWidget = (id: Uuid, title: string, value: number): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: dataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: [DULUX.leads],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (): Promise<Result<RawData>> => dataResult({
            columnMetadata: [{
                id: DULUX.leads,
                displayName: title,
                columnIndex: 0,
                dataType: DatasetColumnDataType.number,
                role: DatasetColumnRole.metric,
            }],
            dataPoints: [[{value}]],
        }),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces: 0,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix: "",
    });

    const makeChartWidget = (
        id: Uuid,
        type: WidgetType.lineGraph | WidgetType.multiBarGraph | WidgetType.stackedBarGraph,
        title: string | null,
        metrics: Uuid[],
        dimensions: Uuid[],
        sourceDataset: Dataset = dataset,
    ): Widget => ({
        id,
        type,
        datasetId: sourceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions,
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
    });

    const makeAidNumberTileWidget = (id: Uuid, title: string, metrics: Uuid[]): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: aidDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces: 0,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix: "",
    });

    return {
        id: uniqueUuid`9eecfaf6-c819-4331-8e40-9ac2eaa669af`,
        title: "Dulux Performance Dashboard",
        sections: [
            {
                id: uniqueUuid`7524173d-f5d5-48e6-bdfc-703100e48935`,
                title: "Key Metrics",
                rows: [
                    {
                        title: "Bluestore KPI cards",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTileWidget(uniqueUuid`60ce8d2a-9de0-4b1b-91b4-9529254c70c4`, "Leads", [DULUX.leads]),
                            makeNumberTileWidget(uniqueUuid`a6ec30dd-f027-4369-a380-ee39e40c323a`, "Walk-ins", [DULUX.walkins]),
                            makeNumberTileWidget(uniqueUuid`8617f7ec-a2fe-4623-8623-49e25d2fb918`, "Calls", [DULUX.calls]),
                            makeNumberTileWidget(uniqueUuid`1017a38d-fa06-411c-8e7a-7a10c8581e93`, "Quotations", [DULUX.quotations]),
                            makeNumberTileWidget(uniqueUuid`4e49eeac-63cc-4dbb-bdaa-36741c38726c`, "Lead to Walk-in Ratio", [DULUX.leadToWalkinRatio], "%", 1),
                            makeNumberTileWidget(uniqueUuid`cffd9119-1d98-4c25-af3b-4f7d35fb489b`, "Lead to Call Ratio", [DULUX.leadToCallRatio], "%", 1),
                        ],
                    },
                    {
                        title: "Operational performance",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeStaticNumberTileWidget(uniqueUuid`d6d4f5b2-5b9e-4f9a-9a3a-0d4eaf1a8d77`, "Bluestores", totalBluestores),
                            makeStaticNumberTileWidget(uniqueUuid`8fb4356e-b0bd-4c65-8fdd-eae563bac7d8`, "JBPs", blueStoreHoKpiCounts.isd),
                            makeStaticNumberTileWidget(uniqueUuid`9645474c-0901-400e-81d9-c67ebb5f7d73`, "MDOs", blueStoreHoKpiCounts.mdo),
                            makeStaticNumberTileWidget(uniqueUuid`77ebb812-d871-4eb2-a863-919f26bd673c`, "RSMs", blueStoreHoKpiCounts.rsm),
                        ],
                    },
                    {
                        title: "AID KPI cards",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeAidNumberTileWidget(uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0001`, "Leads", [AID.leads]),
                            makeAidNumberTileWidget(uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0002`, "Total Enquiries", [AID.totalEnquiries]),
                            makeAidNumberTileWidget(uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0003`, "Onboardings", [AID.onboardings]),
                            makeAidNumberTileWidget(uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0004`, "Appointments", [AID.appointments]),
                        ],
                    },
                ],
            },
            {
                id: uniqueUuid`64a8ef27-b528-40da-a8d1-f8a615181b15`,
                title: "Bluestore Performance Trends",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0005`,
                                WidgetType.multiBarGraph,
                                "Total Leads by Date",
                                [DULUX.leads],
                                [DULUX.date],
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`cf7d704d-8b4e-4ec1-b053-ec2975b23e6c`,
                                WidgetType.lineGraph,
                                "Walk-ins, Calls and Quotations by Date",
                                [DULUX.walkins, DULUX.calls, DULUX.quotations],
                                [DULUX.date],
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`94646244-0854-4cdf-a828-f57dcbe3ec46`,
                                WidgetType.lineGraph,
                                "Lead to Walk-in Rate by Date",
                                [DULUX.leadToWalkinRatio],
                                [DULUX.date],
                            ),
                            makeChartWidget(
                                uniqueUuid`557bc42c-898f-4deb-9a16-9a3166c3ccc0`,
                                WidgetType.lineGraph,
                                "Lead to Call Conversion Rate by Date",
                                [DULUX.leadToCallRatio],
                                [DULUX.date],
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`95b8c67d-04e8-4f1e-964d-8b117fa855bc`,
                                WidgetType.lineGraph,
                                "Leads and Quotations by Date",
                                [DULUX.leads, DULUX.quotations],
                                [DULUX.date],
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`4cc7913f-f5fb-4595-a5ab-ffb5d89819c1`,
                                WidgetType.stackedBarGraph,
                                "Leads by Source by Date",
                                [DULUX.leads],
                                [DULUX.source, DULUX.date],
                            ),
                        ],
                    },
                ],
            },
            {
                id: uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0015`,
                title: "AID Performance Trends",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0011`,
                                WidgetType.lineGraph,
                                "Onboardings, Appointments and Enquiries by Date",
                                [AID.onboardings, AID.appointments, AID.totalEnquiries],
                                [AID.date],
                                aidDataset,
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0012`,
                                WidgetType.lineGraph,
                                "Leads and Appointments by Date",
                                [AID.leads, AID.appointments],
                                [AID.date],
                                aidDataset,
                            ),
                            makeChartWidget(
                                uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0013`,
                                WidgetType.lineGraph,
                                "Leads and Onboardings by Date",
                                [AID.leads, AID.onboardings],
                                [AID.date],
                                aidDataset,
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`6a4dc4b8-2fd6-4a8d-9c8e-75ae7c1d0014`,
                                WidgetType.stackedBarGraph,
                                "Leads by Source by Date",
                                [AID.leads],
                                // Stacked bars use the first dimension as the stack and the second as the x-axis.
                                [AID.source, AID.date],
                                aidDataset,
                            ),
                        ],
                    },
                ],
            },
        ],
    };
}
